/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { badgeColorCode, BadgeOptions } from '../../../components/badge/badge.interface';
import { DataCollectorService } from '../../../core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from '../../../core/services/localisation/zuper.localisation.service';
import { UtilityService } from '../../../core/services/utility_functions/utility_functions.service';
import { PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';

@Component({
  selector: 'quote-data-picker',
  templateUrl: './quote-data-picker.component.html',
  styleUrls: ['./quote-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuoteDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedQuotes: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() quoteUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'created_at';
  @Input() sortType: string = 'DESC';
  @Input() customParams: any = null;

  quotes: any[] = [];
  quotesList: any[] = [];
  quoteEleId: string = '';
  quoteDetails: any = null;
  loaded: boolean = false;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  isSpinLoading: boolean = false;
  currency: string;
  digitsInfo: string = '1.2-2';

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private toast: HotToastService) {
  }

  ngOnInit(): void {
    this.currency = this.utilityService.companyCurrency;
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.showDataPickerPanel();
  }

  rotateArrowIcon(quoteUID: any): any {
    return {
      'rotate-arrow-icon-down': this.isExpandingAction(quoteUID),
      'rotate-arrow-icon-up': !this.isExpandingAction(quoteUID)
    };
  }

  showSpinLoading(quoteUID: any): boolean {
    return this.isExpandingAction(quoteUID) && this.isSpinLoading;
  }

  updateArrowTooltipText(quoteUID: any): string {
    return this.isExpandingAction(quoteUID) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(quoteUID: any): boolean {
    return (this.quoteEleId === 'quote_details_' + quoteUID);
  }

  viewQuoteFullDetails(quoteUID: string): string {
    return '/estimates/' + quoteUID + '/details';
  }

  getStatusBadgeOptions(type: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: type,
      displayType: type,
      color: badgeColorCode[type],
      displayKey: null,
      displayName: this.getProperStatusText(type)
    };
    return badgeOption;
  }

  getProperStatusText(status: string): string {
    if (status === 'AWAIT_PAYMENT' || status === 'AWAIT_RESPONSE') {
      return 'SENT';
    } else if (status === 'YET_TO_SEND') {
      return 'PENDING';
    }
    return status ? status.split('_').join(' ') : '';
  }

  viewQuoteDetails(event: any, quoteUID: string): void {
    event.stopPropagation();
    const elementId: string = 'quote_details_' + quoteUID;
    if (this.quoteEleId === elementId) {
      this.resetQuoteDetails();
    } else {
      this.quoteEleId = elementId;
      this.getQuoteDetails(quoteUID);
    }
  }

  getQuoteDetails(quoteUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.quoteUrl + `/` + quoteUID).subscribe(
      /* eslint-enable quotes */
      (quotes: any) => {
        this.quoteDetails = quotes.data;
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.resetQuoteDetails();
        this.isSpinLoading = false;
      });
  }

  resetQuoteDetails(): void {
    this.quoteEleId = '';
    this.quoteDetails = null;
    this.changeDetectorRef.detectChanges();
  }

  isSelectedQuote(quote: any): boolean {
    if (this.selectedQuotes?.length) {
      return this.selectedQuotes.find(value => value.estimate_uid === quote.estimate_uid) ? true : false;
    }
    return false;
  }

  selectQuote(event: any, quote: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event.checked === true) {
        this.selectedQuotes.push(quote);
      } else {
        this.selectedQuotes = this.selectedQuotes.filter(value => value.estimate_uid !== quote.estimate_uid);
      }
    } else {
      this.selectedQuotes = [quote];
    }
    this.eventEmitter.emit({ value: this.selectedQuotes });
  }

  triggerSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    this.onSearch = true;
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex??0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.quoteEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);

    this.dataCollectorService.getData(this.quoteUrl, urlParams).subscribe(
      (response: any) => {
        this.quotesList = response.data;
        this.quotes = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.quotes.length) {
          this.selectedQuotes = this.selectedQuotes?.length > 0 ? _.uniqBy([...this.selectedQuotes, ...this.filter], 'estimate_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedQuotes });
        } else if (isOpen && this.quotes.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.loaded = true;
      });
  }

  showDataPickerContent(): void {
    const pageIndex: number = this.pageIndex??0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      if (this.filter[0].estimate_no) {
        this.defaultParamObj[this.pickerConfig.params.searchKey] = this.filter[0].estimate_no;
        this.eventEmitter.emit({ searchKey: this.filter[0].estimate_no });
      } else {
        this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].estimate_uid;
      }
    }
  }

  resetQuoteDataPicker(): void {
    this.quotes = [];
  }

  showDataPickerPanel(): void {
    this.resetQuoteDataPicker();
    if (this.showSelectedItems) {
      this.quotes = this.selectedQuotes;
    } else if (this.quotesList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.quotesList.length > 0 || this.searchKey) {
      this.quotes = this.quotesList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedQuotes, filter: this.selectedQuotes });
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedQuotes ? this.selectedQuotes.length : 0,
      displayType: 'listingCount'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }
}
